import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useToast } from '@/components/common/toast';
import { AsyncButton } from '@/components/common/AsyncButton';
import { useServerDataTable } from '@/components/tables/useServerDataTable';
import { DataGrid, type DataGridColumn } from '@/components/tables/DataGrid';
import { Select2 } from '@/components/forms/Select2';
import { ApiError } from '@/api/client/abpError';
import { usePermission } from '@/permissions/usePermission';
import { assetClassesApi } from '@/api/services/onboarding';
import type { AssetClassDto, AssetClassesFiltersDatatableDto, CreateAssetClassDto } from '@/api/types/onboarding';
import { parseFloatOrNull, renderPercentage } from '../helpers';

const P = 'User.Onboarding.AssetClass';

interface NewRow {
  label: string;
  description: string;
  ccg: string;
  ccn: string;
  pfe: string;
  type: string; // parentId as string
}

const emptyRow = (): NewRow => ({ label: '', description: '', ccg: '', ccn: '', pfe: '', type: '' });

export function AssetClassesTab() {
  const toast = useToast();
  const can = usePermission();
  const canCreate = can(`${P}.Create`);
  const canDownloadExcel = can(`${P}.DownloadExcel`);

  const parents = useQuery({
    queryKey: ['onboarding', 'asset-classes', 'parents'],
    queryFn: assetClassesApi.getParentAssetClasses,
  });

  const [newRow, setNewRow] = useState<NewRow | null>(null);
  const [saving, setSaving] = useState(false);
  const [ddParent, setDdParent] = useState<HTMLElement | null>(null);

  const table = useServerDataTable<AssetClassDto, Partial<AssetClassesFiltersDatatableDto>>({
    queryKey: ['onboarding', 'asset-classes'],
    fetcher: (req) => assetClassesApi.getListForDataTable(req as AssetClassesFiltersDatatableDto),
    pageSize: 5,
  });

  const startCreate = () => {
    if (newRow) {
      toast.warning('Please save or cancel the current new row before creating another.');
      return;
    }
    setNewRow(emptyRow());
  };

  const saveNew = async () => {
    if (!newRow) return;
    setSaving(true);
    try {
      const payload: CreateAssetClassDto = {
        label: newRow.label,
        description: newRow.description,
        ccg: parseFloatOrNull(newRow.ccg),
        ccn: parseFloatOrNull(newRow.ccn),
        pfe: parseFloatOrNull(newRow.pfe),
        parentId: newRow.type === '' ? null : Number(newRow.type),
      };
      await assetClassesApi.create(payload);
      toast.success('New Item Added Successfully');
      setNewRow(null);
      table.refetch();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Failed to add asset class.');
    } finally {
      setSaving(false);
    }
  };

  const columns = useMemo<DataGridColumn<AssetClassDto>[]>(
    () => [
      { key: 'label', title: 'Asset Class', className: 'text-start', value: (r) => r.label ?? '' },
      { key: 'description', title: 'Description', className: 'text-start', value: (r) => r.description ?? '' },
      { key: 'ccg', title: 'Gross', className: 'text-start', value: (r) => r.ccg, cell: (r) => renderPercentage(r.ccg) },
      { key: 'ccn', title: 'Net', className: 'text-start', value: (r) => r.ccn, cell: (r) => renderPercentage(r.ccn) },
      { key: 'pfe', title: 'Potential Fututre Exposure', className: 'text-start', value: (r) => r.pfe, cell: (r) => renderPercentage(r.pfe) },
      { key: 'type', title: 'Type', className: 'text-start', value: (r) => r.type ?? '' },
    ],
    [],
  );

  const toolbar = canCreate ? (
    <AsyncButton className="btn btn-primary btn-sm" icon="fa fa-plus" title="Create new record" onClick={startCreate}>
      Create New
    </AsyncButton>
  ) : null;

  // The new row is rendered as a DataGrid footerTop <tr> so it sits above the data.
  const parentOptions = parents.data ?? [];
  const colSpanHead = columns.length;

  const footerTop = newRow ? (
    <tr className="onb-new-row">
      <td>
        <input className="form-control form-control-sm" placeholder="Asset Class" value={newRow.label} disabled={saving} onChange={(e) => setNewRow({ ...newRow, label: e.target.value })} />
      </td>
      <td>
        <input className="form-control form-control-sm" placeholder="Description" value={newRow.description} disabled={saving} onChange={(e) => setNewRow({ ...newRow, description: e.target.value })} />
      </td>
      <td><input className="form-control form-control-sm" placeholder="Gross" value={newRow.ccg} disabled={saving} onChange={(e) => setNewRow({ ...newRow, ccg: e.target.value })} /></td>
      <td><input className="form-control form-control-sm" placeholder="Net" value={newRow.ccn} disabled={saving} onChange={(e) => setNewRow({ ...newRow, ccn: e.target.value })} /></td>
      <td><input className="form-control form-control-sm" placeholder="PFE" value={newRow.pfe} disabled={saving} onChange={(e) => setNewRow({ ...newRow, pfe: e.target.value })} /></td>
      <td>
        <div className="d-flex gap-1 align-items-center">
          <div className="flex-grow-1">
            <Select2
              value={newRow.type}
              options={parentOptions.map((p) => ({ value: String(p.id), label: p.label ?? '' }))}
              placeholder="—"
              searchable={false}
              disabled={saving}
              dropdownParent={ddParent}
              onChange={(v) => setNewRow({ ...newRow, type: v ?? '' })}
            />
          </div>
          <AsyncButton className="btn btn-sm btn-outline-primary" icon="fa fa-check" title="Save" onClick={saveNew} disabled={saving} />
          <button type="button" className="btn btn-sm btn-outline-danger" title="Cancel" disabled={saving} onClick={() => setNewRow(null)}><i className="fa fa-times" /></button>
        </div>
      </td>
    </tr>
  ) : (
    <tr style={{ display: 'none' }}><td colSpan={colSpanHead} /></tr>
  );

  return (
    <div ref={setDdParent}>
      <DataGrid
        columns={columns}
        data={table.rows}
        rowKey={(r) => String(r.id)}
        card
        columnSearch
        columnSearchValues={table.columnFilters}
        onColumnSearchChange={table.setColumnFilter}
        toolbarStart={toolbar}
        includeExcel={canDownloadExcel}
        excelFileName="Asset Classes"
        footerTop={newRow ? footerTop : undefined}
        emptyMessage={table.isLoading ? 'Loading…' : 'No data available'}
        server={{
          pageIndex: table.pageIndex,
          pageSize: table.pageSize,
          pageCount: table.pageCount,
          totalCount: table.totalCount,
          onPageChange: table.setPageIndex,
          onPageSizeChange: table.setPageSize,
        }}
      />
    </div>
  );
}
